import React from 'react';
import { matchDriver, isQualifying, getFastestRacer, getLastBestColumnColor, getIntervalBetweenRacers, getStandingsBestOrLastTime, getRelDiff } from './Utils';
import { Stack } from '@mui/material';
import { Divider } from '@mui/material';
import { useEffect } from 'react';
import { Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useMediaQuery } from '@mui/material';
import ClassPositionBest from './ClassPositionBest';
import LapsTime from './LapsTime';
import NearDriver from './NearDriver';
import BestVsLeaderBest from './BestVsLeaderBest';
import FirstTimeUserExperience from './FirstTimeUserExperience';
import NearViewDriver from './NearViewDriver';
import PositionAndBest from './PositionAndBest';

const NearView = ( {racers, classRacers, race, driver, headerHeight, raceState, isOverall} ) => {
    const [prevDriver, setPrevDriver] = React.useState(null);
    const [curDriver, setCurDriver] = React.useState(null);
    const [nextDriver, setNextDriver] = React.useState(null);
    const [position, setPosition] = React.useState(null);
    const [fastestRacer, setFastestRacer] = React.useState(null);
    const [leaderRacer, setLeaderRacer] = React.useState(null);

    const theme = useTheme();
    const isSmall = useMediaQuery(theme.breakpoints.down('sm')); // < 600px, iPhone portrait
    const isMoreThanSmall = useMediaQuery(theme.breakpoints.up('sm')); // >= 600px, iPhone landscape or tablet
    const isTall = useMediaQuery('(min-height:500px)'); // >= 500px height, iPhone portrait or tablet

    useEffect(() => {
        if (!racers || !driver || racers.length == 0 || driver.length == 0) {
            return;
        }

        var myRacerIndex = -1;
        for (var i = 0; i<racers.length; i++) {
            if (matchDriver(driver, racers[i].name)) {
                myRacerIndex = i;
                break;
            }
        }

        if (myRacerIndex == -1) {
            setCurDriver(null);
            setPosition(null);
            setFastestRacer(null);
            setPrevDriver(null);
            setNextDriver(null);
            return;
        }

        setCurDriver(racers[myRacerIndex]);

        var myRacerClassIndex = -1;
        for (var j = 0; j<classRacers[racers[myRacerIndex].class].racers.length; j++) {
            if (matchDriver(driver, classRacers[racers[myRacerIndex].class].racers[j].name)) {
                myRacerClassIndex = j;
                break;
            }
        }

        if (myRacerClassIndex == -1) {
            console.log("ClassView: class racer not found");
            setPosition(null);
            setFastestRacer(null);
            setPrevDriver(null);
            setNextDriver(null);
            return;
        }  
        
        let effectiveRacerIndex = isOverall ? myRacerIndex : myRacerClassIndex;
        let effectiveRacers = isOverall ? racers : classRacers[racers[myRacerIndex].class].racers;

        setPosition(effectiveRacerIndex + 1);

        setFastestRacer(isOverall ? getFastestRacer(racers) : getFastestRacer(classRacers[racers[myRacerIndex].class].racers));
        setLeaderRacer(isOverall ? racers[0] : classRacers[racers[myRacerIndex].class].racers[0]);

        if (effectiveRacerIndex > 0) {
            setPrevDriver(effectiveRacers[effectiveRacerIndex - 1]);
        }
        else {
            setPrevDriver(null);
        }

        if (effectiveRacerIndex < effectiveRacers.length - 1) {
            setNextDriver(effectiveRacers[effectiveRacerIndex + 1]);
        }
        else {
            setNextDriver(null);
        }
    }, [racers, driver, classRacers]);

    return (
        <>
            { !driver ? <FirstTimeUserExperience message="To use this view, select a driver to follow using the top right menu."/> : null }
            { driver && !curDriver ? <FirstTimeUserExperience message={`No driver in the current race matches "${driver}". Update name or please wait for further race results.`}/> : null }
            { curDriver ? (<Stack spacing={(isMoreThanSmall && !isTall) ? 0 : 1} 
                direction="column" 
                justifyContent="flex-start" 
                alignItems="stretch" 
                divider={<Divider orientation="horizontal" flexItem />}
                sx={{ height: `calc(100% - ${headerHeight}px)`, paddingTop: (isMoreThanSmall && !isTall) ? 0 : 2 }}
                >
                <Box>
                        { (isSmall) && (<>
                            <Stack direction="column" justifyContent="space-between" alignItems="stretch" spacing={2}>
                                <Stack direction="row" justifyContent="space-between" alignItems="stretch" spacing={2} sx={{ flexGrow: 1 }}>
                                    <PositionAndBest curDriver={curDriver} fastestClassRacer={fastestRacer} classPosition={position} classRacers={classRacers} isOverall={isOverall} /> 
                                    <Divider orientation="horizontal" flexItem />
                                </Stack>
                                <Stack direction="row" justifyContent="space-between" alignItems="stretch" spacing={2} sx={{ flexGrow: 1 }}>
                                    <BestVsLeaderBest curDriver={curDriver} leaderRacer={leaderRacer} raceState={raceState} />
                                    <Divider orientation="vertical" flexItem />
                                    <LapsTime curDriver={curDriver} raceState={raceState} excludeTime={true} />
                                </Stack>
                            </Stack>                            
                        </>)}
                        { (isMoreThanSmall) && (<>
                            <Stack direction="row" justifyContent="space-between" alignItems="stretch" spacing={2} sx={{ flexGrow: 1 }}>
                                <PositionAndBest curDriver={curDriver} fastestClassRacer={fastestRacer} classPosition={position} classRacers={classRacers} isOverall={isOverall} />
                                <Divider orientation="vertical" flexItem /> 
                                <BestVsLeaderBest curDriver={curDriver} leaderRacer={leaderRacer} raceState={raceState} />
                                <Divider orientation="vertical" flexItem /> 
                                <LapsTime curDriver={curDriver} raceState={raceState} />
                            </Stack>
                        </>)}
                </Box>
                { isMoreThanSmall && isTall && (
                    <Stack spacing={3} direction="column" justifyContent="flex-start" alignItems="stretch" sx={{ flexGrow: 1, overflow: 'auto' }}>
                        <NearViewDriver curDriver={curDriver} position={position - 1} referenceDriver={prevDriver} classRacers={classRacers} raceState={raceState} />
                        <Divider orientation="horizontal" flexItem />
                        <NearViewDriver curDriver={curDriver} position={position + 1} referenceDriver={nextDriver} classRacers={classRacers} raceState={raceState} />
                    </Stack>
                ) }
                { isMoreThanSmall && !isTall && (
                    <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
                        <Stack spacing={1} direction="row" justifyContent="space-between" alignItems="stretch">
                            <NearDriver curDriver={curDriver} otherDriver={nextDriver} classPosition={position} raceState={raceState} isAhead={false} />
                            <Divider orientation="vertical" flexItem />
                            <NearDriver curDriver={curDriver} otherDriver={prevDriver} classPosition={position} raceState={raceState} isAhead={true} />
                        </Stack>
                    </Box>
                ) }
                { isSmall && (
                <Box>
                    <Stack spacing={1} direction="column" justifyContent="space-between" alignItems="stretch">
                        <NearDriver curDriver={curDriver} otherDriver={prevDriver} classPosition={position} raceState={raceState} isAhead={true} />
                        <Divider orientation="horizontal" flexItem />
                        <NearDriver curDriver={curDriver} otherDriver={nextDriver} classPosition={position} raceState={raceState} isAhead={false} />
                    </Stack>
                </Box>
                )}
            </Stack>) : null }
        </>
    );
};

export default NearView;
